"use client";

import Head from 'next/head';
import { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useTheme } from '@/app/providers';
import { useAuth } from '@/contexts/AuthContext';
import { useRightPanel } from '@/contexts/RightPanelContext';
import { apiService } from '@/lib/api';
import { Menu, X, Moon, Sun, Home, LogIn, LogOut, ChevronDown } from 'lucide-react';
import Image from 'next/image';
import ReminderDropdown from './ReminderDropdown';

const navigation = [
  { name: 'Home', href: '/', icon: Home },
  { name: 'Kundali', href: 'https://kundali.swastishanti.com', external: true },
  { name: 'Services', href: '/services' },
  { name: 'Appointment', href: '/appointments', highlight: false },
  { name: 'About Us', href: '/about-us' },
  { name: 'Blog', href: '/blog' },
  { name: 'Date Converter', href: '/date-converter' },
  { name: 'Store', href: '/store' },
];

function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const { theme, toggleTheme } = useTheme();
  const { isAuthenticated, user, loading, logout } = useAuth();
  const { openPanel } = useRightPanel();
  const [navUserMenuOpen, setNavUserMenuOpen] = useState(false);
  const isUserLoggedIn = isAuthenticated || !!user;
  const isAdminUser = !!user && (user.isAdmin || user.role === 'admin');
  const isModeratorUser = !!user && user.role === 'moderator';

  // Measure the visible nav's real height into --main-nav-height so the layout
  // spacer matches the header exactly (previously it reserved a fixed 64/98px,
  // which over-shot and left a gap under the navbar). Only one nav is displayed
  // at a time (responsive), so the hidden one reports offsetHeight 0.
  const desktopNavRef = useRef<HTMLElement>(null);
  const mobileNavRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const heights = { desktop: 0, mobile: 0 };
    const applyMax = () => {
      const h = Math.max(heights.desktop, heights.mobile);
      if (h) document.documentElement.style.setProperty('--main-nav-height', `${h}px`);
    };
    const readFromOffset = () => {
      heights.desktop = desktopNavRef.current?.offsetHeight || 0;
      heights.mobile = mobileNavRef.current?.offsetHeight || 0;
      applyMax();
    };
    // Deferred to the next frame so this initial read doesn't force a
    // synchronous layout flush mid-commit, right when other effects may
    // just have mutated the DOM in the same cycle.
    const raf = requestAnimationFrame(readFromOffset);
    window.addEventListener('resize', readFromOffset);
    // ResizeObserver already computes the box size as part of the browser's
    // own layout pass - reading entry.borderBoxSize here (instead of calling
    // .offsetHeight again inside the callback) avoids forcing a second,
    // redundant synchronous layout just to get the same number.
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver((entries) => {
      for (const entry of entries) {
        const h = entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height;
        if (entry.target === desktopNavRef.current) heights.desktop = h;
        else if (entry.target === mobileNavRef.current) heights.mobile = h;
      }
      applyMax();
    }) : null;
    if (ro) {
      if (desktopNavRef.current) ro.observe(desktopNavRef.current);
      if (mobileNavRef.current) ro.observe(mobileNavRef.current);
    }
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', readFromOffset);
      ro?.disconnect();
    };
  }, [mounted]);

  const trackNavClick = (label: string, targetHref: string, action: string = 'nav_click', component: string = 'navbar') => {
    apiService.trackUserCrawl({
      eventType: 'nav_click',
      pagePath: pathname || '/',
      section: 'navbar',
      category: label,
      action,
      metadata: {
        component,
        targetHref,
        currentPath: pathname || '/',
        loggedIn: isUserLoggedIn,
      },
    }).catch(() => {});
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  // Optimized scroll handler with throttling
  useEffect(() => {
    let ticking = false;
    let lastScrollY = 0;
    const SCROLL_THRESHOLD = 20;
    
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          // Only update state if threshold is crossed to reduce re-renders
          if (Math.abs(currentScrollY - lastScrollY) > 5) {
            setScrolled(currentScrollY > SCROLL_THRESHOLD);
            lastScrollY = currentScrollY;
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Compute user initials for avatar fallback
  const userInitials = useMemo(() => {
    if (!user) return '';
    if (user.name) {
      return user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase();
    }
    if (user.email) {
      return user.email[0].toUpperCase();
    }
    return '';
  }, [user]);

  const userShortName = useMemo(() => {
    if (!user) return '';
    if (user.name?.trim()) {
      return user.name.trim().split(' ')[0];
    }
    if (user.email) {
      return user.email.split('@')[0];
    }
    return userInitials || 'Account';
  }, [user, userInitials]);

  return (
    <>
      <Head>
        <link rel="icon" href="/favv.png" type="image/png" />
      </Head>
      {/* Desktop Navigation - Sticks below top info bar without overlap */}
      <nav
        ref={desktopNavRef}
        className="hidden md:grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center fixed left-0 right-0 z-[120] border-b border-gray-200 dark:border-gray-700 w-full px-4 lg:px-8 overflow-visible"
        style={{
          top: 'var(--top-nav-height,0px)',
          marginTop: 0,
          marginBottom: 0,
          width: '100%',
          height: '90px',
          paddingTop: '0',
          paddingBottom: '0',
          backgroundColor: '#fff',
        }}
      >
        <div className="flex items-center justify-start min-w-0">
          <Link href="/" aria-label="Swastishanti Home" className="flex items-center gap-2">
            <Image
              //src="/mainlogo3.avif"
              src="/swastishanti-logo.png"
              alt="Swasti Shanti Main Logo"
              width={80}
              height={80}
              className="w-20 h-20 object-contain bg-white rounded-lg border border-gray-200 p-0 m-0"
              style={{ background: '#fffafa', borderRadius: '12px', border: '1px solid #f7f2f2', padding: 0, margin: 0 }}
              priority
            />
            <span className="hidden 2xl:inline-block font-bold text-2xl text-primary-700 dark:text-primary-300 whitespace-nowrap">
              Swastishanti
            </span>
          </Link>
        </div>

        <div className="flex items-center justify-end gap-1 lg:gap-2 xl:gap-3 min-w-0 w-full pr-8">
          {navigation.map((item) => {
          const isActive = pathname === item.href;
          const baseColorClass = item.highlight
            ? ''
            : isActive
              ? 'text-primary-700 dark:text-primary-300 font-bold underline underline-offset-8 decoration-2 decoration-primary-700 dark:decoration-primary-300'
              : 'text-gray-700 dark:text-gray-300 hover:text-primary-700 dark:hover:text-primary-300';
          const highlightClass = item.highlight
            ? 'bg-primary-700 !text-white hover:bg-primary-800'
            : '';
            if (item.href === '/appointments') {
              return (
                <Link
                  key={item.name}
                  href="/appointments"
                  onClick={() => trackNavClick(item.name, '/appointments', 'nav_appointments', 'desktop_nav')}
                  className={`rounded-lg font-semibold transition-all whitespace-nowrap relative z-10 flex items-center justify-center h-9 px-2 lg:px-3 text-sm lg:text-[15px] ${baseColorClass} ${highlightClass}`}
                >
                  {item.icon && (
                    <item.icon className="w-4 h-4 mr-1 inline-block flex-shrink-0" aria-hidden="true" width={16} height={16} />
                  )}
                  {item.name}
                </Link>
              );
            }
            if (item.external) {
              const getExternalHref = () => {
                const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
                return token ? `${item.href}?token=${token}` : item.href;
              };
              return (
                <a
                  key={item.name}
                  href={getExternalHref()}
                  onClick={() => trackNavClick(item.name, item.href, 'nav_click', 'desktop_nav')}
                  className={`rounded-lg font-semibold transition-all whitespace-nowrap relative z-10 flex items-center justify-center h-9 px-2 lg:px-3 text-sm lg:text-[15px] ${baseColorClass} ${highlightClass}`}
                >
                  {item.name}
                </a>
              );
            }
            return (
              <Link
                key={item.name}
                href={item.href}
                onClick={() => trackNavClick(item.name, item.href, 'nav_click', 'desktop_nav')}
                className={`rounded-lg font-semibold transition-all whitespace-nowrap relative z-10 flex items-center justify-center h-9 px-2 lg:px-3 text-sm lg:text-[15px] ${baseColorClass} ${highlightClass}`}
              >
                {item.icon && (
                  <item.icon className="w-4 h-4 mr-1 inline-block flex-shrink-0" aria-hidden="true" width={16} height={16} />
                )}
                {item.name}
              </Link>
            );
          })}
        </div>

        <div className="flex items-center justify-end min-w-0">
          {isUserLoggedIn ? (
          <div className="relative">
            <button
              type="button"
              onClick={() => setNavUserMenuOpen((prev) => !prev)}
              className="flex items-center gap-2 rounded-lg font-semibold transition-all whitespace-nowrap relative z-10 h-9 px-2 lg:px-3 text-sm lg:text-[15px] bg-gray-100 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 hover:bg-gray-200 dark:hover:bg-dark-600"
              aria-label="Open user menu"
            >
              {user?.profileImage ? (
                <Image
                  src={user.profileImage}
                  alt={user?.name || user?.email || 'User avatar'}
                  width={28}
                  height={28}
                  className="w-7 h-7 rounded-full object-cover border-2 border-primary-500"
                  loading="lazy"
                  unoptimized
                />
              ) : (
                <span className="w-7 h-7 rounded-full bg-primary-600 text-white flex items-center justify-center text-xs font-bold border-2 border-primary-400">
                  {userInitials || 'U'}
                </span>
              )}
              <span className="max-w-[100px] truncate text-gray-800 dark:text-gray-100 font-semibold">{userShortName || user?.email?.split('@')[0] || 'User'}</span>
              <ChevronDown
                className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${navUserMenuOpen ? 'rotate-180' : ''}`}
                width={16}
                height={16}
              />
            </button>

            {navUserMenuOpen && (
              <div className="absolute right-0 mt-2 w-64 rounded-xl bg-white dark:bg-dark-800 shadow-brand-md border border-gray-100 dark:border-gray-700 overflow-hidden z-[140]">
                <Link
                  href="/profile"
                  className={`block px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 transition-colors ${
                    isAdminUser || isModeratorUser ? 'border-b border-gray-100 dark:border-gray-700' : ''
                  }`}
                  onClick={() => {
                    trackNavClick('Manage Profile', '/profile', 'nav_click', 'desktop_user_menu');
                    setNavUserMenuOpen(false);
                  }}
                >
                  Manage Profile
                </Link>
                {isAdminUser ? (
                  <Link
                    href="/admin"
                    className="block px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    onClick={() => {
                      trackNavClick('Admin', '/admin', 'nav_click', 'desktop_user_menu');
                      setNavUserMenuOpen(false);
                    }}
                  >
                    Admin
                  </Link>
                ) : isModeratorUser ? (
                  <Link
                    href="/admin?section=blog&tab=moderation"
                    className="block px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                    onClick={() => {
                      trackNavClick('Blog Moderation', '/admin?section=blog&tab=moderation', 'nav_click', 'desktop_user_menu');
                      setNavUserMenuOpen(false);
                    }}
                  >
                    Blog Moderation
                  </Link>
                ) : null}
                {/* ReminderDropdown removed as email reminder is not necessary in notification */}
                <button
                  onClick={() => {
                    trackNavClick('Logout', '/logout', 'logout_click', 'desktop_user_menu');
                    setNavUserMenuOpen(false);
                    logout();
                  }}
                  className={`w-full text-left px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex items-center gap-2 ${
                    isAdminUser || isModeratorUser ? 'border-t border-gray-100 dark:border-gray-700' : ''
                  }`}
                >
                  <LogOut className="w-4 h-4 flex-shrink-0" width={16} height={16} />
                  Logout
                </button>
              </div>
            )}
          </div>
          ) : !loading ? (
          <Link href="/login" className="flex items-center gap-2 rounded-lg font-semibold transition-all whitespace-nowrap relative z-10 h-9 px-2 lg:px-3 text-sm lg:text-[15px] border border-primary-700 text-primary-700 dark:text-primary-300 dark:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 focus:outline-none focus:ring-2 focus:ring-primary-500">
            <LogIn className="w-4 h-4 flex-shrink-0" width={16} height={16} /> Login
          </Link>
          ) : null}
          <button
            onClick={() => toggleTheme(theme === 'light' ? 'dark' : 'light')}
            className="p-2 rounded-lg transition-colors text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 ml-2"
            aria-label="Toggle theme"
            disabled={!mounted}
            style={{lineHeight: '40px', height: '40px', display: 'flex', alignItems: 'center'}}
          >
            {mounted && theme === 'light' ? (
              <Sun className="w-5 h-5 flex-shrink-0" width={20} height={20} />
            ) : mounted && theme === 'dark' ? (
              <Moon className="w-5 h-5 flex-shrink-0" width={20} height={20} />
            ) : (
              <Sun className="w-5 h-5 flex-shrink-0 text-white" width={20} height={20} />
            )}
          </button>
        </div>
      </nav>
      {/* Mobile Navigation Bar - Always visible on mobile, ensure welcome section is visible */}
      <nav ref={mobileNavRef} className="flex md:hidden items-center w-full px-4 py-2 border-b border-[#1e3a5f] shadow-brand-sm fixed top-[var(--top-nav-height,0px)] left-0 right-0 z-[130]" style={{ background: 'linear-gradient(to right, #0f2744, #1a3a6b)' }}>
        <div className="flex w-full items-center justify-between">
          <button
            onClick={() => router.push('/')}
            className="flex items-center font-bold text-base sm:text-lg focus:outline-none bg-transparent border-none"
            aria-label="Swastishanti Home"
            style={{ background: 'none', border: 'none', cursor: 'pointer', zIndex: 1 }}
          >
            <Image
              src="/swastishanti-logo.png"
              alt="Swasti Shanti Logo"
              width={40}
              height={40}
              className="w-10 h-10 object-contain rounded-lg mr-2"
              style={{ background: '#fff', borderRadius: '8px', padding: '2px', marginRight: '0.5rem' }}
              priority
            />
            <span className="text-white font-bold text-base">Swastishanti</span>
          </button>

          <div className="flex items-center gap-1.5">
            {isUserLoggedIn ? (
              <Link
                href="/profile"
                onClick={() => trackNavClick('Manage Profile', '/profile', 'nav_click', 'mobile_topbar')}
                className="flex items-center justify-center w-8 h-8 rounded-full bg-white/20 hover:bg-white/30"
                aria-label="Go to profile"
              >
                {user?.profileImage ? (
                  <Image
                    src={user.profileImage}
                    alt={user?.name || user?.email || 'User avatar'}
                    width={32}
                    height={32}
                    className="w-8 h-8 rounded-full object-cover border border-white/40"
                    loading="lazy"
                    unoptimized
                  />
                ) : (
                  <span className="text-white text-xs font-bold">{userInitials || 'A'}</span>
                )}
              </Link>
            ) : null}
            {!isUserLoggedIn && !loading && (
              <Link
                href="/login"
                onClick={() => trackNavClick('Login', '/login', 'nav_click', 'mobile_topbar')}
                className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold text-white bg-white/20 hover:bg-white/30"
                aria-label="Go to login"
              >
                <LogIn className="w-4 h-4 flex-shrink-0" width={16} height={16} />
                Login
              </Link>
            )}
            <button
              onClick={() => toggleTheme(theme === 'light' ? 'dark' : 'light')}
              className="p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
              aria-label="Toggle theme"
              disabled={!mounted}
              style={{ zIndex: 2 }}
            >
              {mounted && theme === 'light' ? (
                <Sun className="w-5 h-5 flex-shrink-0 text-white" width={20} height={20} />
              ) : mounted && theme === 'dark' ? (
                <Moon className="w-5 h-5 flex-shrink-0 text-white" width={20} height={20} />
              ) : (
                <Sun className="w-5 h-5 flex-shrink-0 text-white" width={20} height={20} />
              )}
            </button>
            {/* Hamburger right */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors text-white"
              aria-label="Toggle menu"
              style={{ zIndex: 2 }}
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 flex-shrink-0" width={24} height={24} />
              ) : (
                <Menu className="w-6 h-6 flex-shrink-0" width={24} height={24} />
              )}
            </button>
          </div>
        </div>
      </nav>
      {/* Mobile Menu Drawer - position below mobile nav bar */}
      <div
        className={`md:hidden fixed top-[calc(var(--top-nav-height,0px)+3.5rem)] left-0 right-0 z-[120] bg-white dark:bg-dark-900 border-b border-gray-200 dark:border-gray-700 shadow-brand-lg transition-all duration-300 ${
          mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none'
        }`}
      >
        <nav className="px-4 py-4 relative max-h-[calc(100vh-3.5rem)] overflow-y-auto">
          <div className="flex flex-col gap-2 pt-2">

            {navigation.map((item) => {
              const isActive = pathname === item.href;
              const baseColorClass = item.highlight
                ? ''
                : isActive
                  ? 'text-primary-700 dark:text-primary-300 font-bold bg-primary-50 dark:bg-primary-900/20'
                  : 'text-gray-700 dark:text-gray-300 hover:text-primary-700 dark:hover:text-primary-300';
              const highlightClass = item.highlight
                ? 'bg-primary-700 !text-white hover:bg-primary-800'
                : '';
              if (item.href === '/appointments') {
                return (
                  <Link
                    key={item.name}
                    href="/appointments"
                    onClick={() => { setMobileMenuOpen(false); trackNavClick(item.name, '/appointments', 'nav_appointments', 'mobile_menu'); }}
                    className={`w-full rounded-lg font-semibold transition-all text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-dark-800 ${baseColorClass} ${highlightClass}`}
                    style={{ fontSize: '1rem', minHeight: '44px', padding: '0 14px', display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}
                  >
                    {item.icon && (
                      <item.icon className="w-5 h-5 mr-2 inline-block flex-shrink-0" aria-hidden="true" width={20} height={20} />
                    )}
                    {item.name}
                  </Link>
                );
              }
              if (item.external) {
                const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : null;
                const mobileHref = token ? `${item.href}?token=${token}` : item.href;
                return (
                  <a
                    key={item.name}
                    href={mobileHref}
                    onClick={() => {
                      trackNavClick(item.name, item.href, 'nav_click', 'mobile_menu');
                      setMobileMenuOpen(false);
                    }}
                    className={`w-full rounded-lg font-semibold transition-all ${baseColorClass} ${highlightClass}`}
                    style={{ fontSize: '0.98rem', minHeight: '42px', padding: '0 14px', display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}
                  >
                    {item.name}
                  </a>
                );
              }
              return (
                <Link
                  key={item.name}
                  href={item.href}
                  onClick={() => {
                    trackNavClick(item.name, item.href, 'nav_click', 'mobile_menu');
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full rounded-lg font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-dark-800 ${baseColorClass} ${highlightClass}`}
                  style={{ fontSize: '0.98rem', minHeight: '42px', padding: '0 14px', display: 'flex', alignItems: 'center', marginBottom: '0.25rem' }}
                >
                  {item.icon && (
                    <item.icon className="w-4 h-4 mr-2 inline-block flex-shrink-0" aria-hidden="true" width={16} height={16} />
                  )}
                  {item.name}
                </Link>
              );
            })}
            {isUserLoggedIn ? (
              <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 mt-2 space-y-2">
                <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                  {user?.name || userInitials}
                </div>
                <Link
                  href="/profile"
                  onClick={() => {
                    trackNavClick('Manage Profile', '/profile', 'nav_click', 'mobile_user_menu');
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 rounded-lg font-medium text-gray-800 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 transition-all"
                >
                  Manage Profile
                </Link>
                {isAdminUser ? (
                  <Link
                    href="/admin"
                    onClick={() => {
                      trackNavClick('Admin', '/admin', 'nav_click', 'mobile_user_menu');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 rounded-lg font-medium text-gray-800 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 transition-all"
                  >
                    Admin
                  </Link>
                ) : isModeratorUser ? (
                  <Link
                    href="/admin?section=blog&tab=moderation"
                    onClick={() => {
                      trackNavClick('Blog Moderation', '/admin?section=blog&tab=moderation', 'nav_click', 'mobile_user_menu');
                      setMobileMenuOpen(false);
                    }}
                    className="w-full text-left px-4 py-2 rounded-lg font-medium text-gray-800 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 transition-all"
                  >
                    Blog Moderation
                  </Link>
                ) : null}
                {/* ReminderDropdown removed as email reminder is not necessary in notification */}
                <button
                  onClick={() => {
                    trackNavClick('Logout', '/logout', 'logout_click', 'mobile_user_menu');
                    logout();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left px-4 py-2 rounded-lg font-medium text-gray-800 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 transition-all flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4 flex-shrink-0" width={16} height={16} />
                  Logout
                </button>
              </div>
            ) : !loading ? (
              <Link
                href="/login"
                onClick={() => {
                  trackNavClick('Login', '/login', 'nav_click', 'mobile_user_menu');
                  setMobileMenuOpen(false);
                }}
                className="px-4 py-3 rounded-lg font-semibold text-gray-800 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 transition-all border-t border-gray-200 dark:border-gray-700 mt-2 flex items-center gap-2"
              >
                <LogIn className="w-4 h-4 flex-shrink-0" width={16} height={16} />
                Login
              </Link>
            ) : null}
          </div>
        </nav>
      </div>
    </>
  );
}

// Memoize Header to prevent unnecessary re-renders
export default memo(Header);
